import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Calendar, Clock, Lock, LogOut, Trash2, User, ShieldAlert, Users } from "lucide-react";
import logo from "@/assets/logo-mente-sa.png";
import { generateTimeSlotsForDay, type WeekSchedule } from "@/hooks/use-working-hours";
import { STAFF_ROLE_PRESETS, type StaffAccessRole } from "@/lib/staff-permissions";

interface Booking {
  id: string;
  client_name: string;
  booking_date: string;
  booking_time: string;
  location_type: string;
  status: string;
  services: { name: string } | null;
  staff_id?: string | null;
  client_email?: string | null;
  client_phone?: string | null;
}

const statusLabels: Record<string, string> = {
  pending: "Pendente",
  confirmed: "Confirmada",
  on_the_way: "A Caminho",
  completed: "Concluída",
  cancelled: "Recusada",
  blocked: "Bloqueado",
};

const statusColors: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  on_the_way: "bg-blue-100 text-blue-800",
  cancelled: "bg-red-100 text-red-800",
  completed: "bg-primary/10 text-primary",
  blocked: "bg-muted text-muted-foreground",
};

const StaffPortal = () => {
  const { user, isStaff, staffId, staffStatus, staffRole, can, loading, rolesLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [staffName, setStaffName] = useState("");
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [filter, setFilter] = useState("confirmed");
  const [schedule, setSchedule] = useState<WeekSchedule | null>(null);

  // Block states
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockDate, setBlockDate] = useState("");
  const [blockTimes, setBlockTimes] = useState<string[]>([]);
  const [bookedSlotsForBlock, setBookedSlotsForBlock] = useState<string[]>([]);
  const [processing, setProcessing] = useState(false);

  const [scope, setScope] = useState<"mine" | "all">("mine");
  const canViewAll = can("view_all_bookings");
  const canBlock = can("block_own_agenda");

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [user, loading, navigate]);

  // Fetch staff info
  useEffect(() => {
    if (!staffId) return;
    supabase.from("staff").select("name, working_hours").eq("id", staffId).single()
      .then(({ data }) => {
        if (data) {
          setStaffName(data.name);
          setSchedule(
            typeof data.working_hours === "string"
              ? JSON.parse(data.working_hours)
              : data.working_hours as unknown as WeekSchedule
          );
        }
      });
  }, [staffId]);

  // Fetch bookings
  useEffect(() => {
    if (!staffId) return;
    let query = supabase
      .from("bookings")
      .select("id, client_name, booking_date, booking_time, location_type, status, staff_id, client_email, client_phone, services:service_id(name)")
      .eq("status", filter)
      .order("booking_date", { ascending: true });
    if (!(canViewAll && scope === "all")) query = query.eq("staff_id", staffId);
    query.then(({ data }) => setBookings((data as any[]) || []));
  }, [staffId, filter, scope, canViewAll]);

  // Fetch booked slots for block dialog
  useEffect(() => {
    if (!blockDate || !staffId) { setBookedSlotsForBlock([]); return; }
    supabase.rpc("get_booked_slots", { p_date: blockDate, p_staff_id: staffId })
      .then(({ data }) => setBookedSlotsForBlock((data as string[]) || []));
  }, [blockDate, staffId]);

  const getTimeSlotsForDateStr = (dateStr: string): string[] => {
    if (!dateStr || !schedule) return [];
    const date = new Date(dateStr + "T12:00:00");
    const daySchedule = schedule[date.getDay().toString()];
    if (!daySchedule?.enabled) return [];
    return generateTimeSlotsForDay(daySchedule);
  };

  const toggleBlockTime = (slot: string) => {
    setBlockTimes(prev => prev.includes(slot) ? prev.filter(t => t !== slot) : [...prev, slot]);
  };

  const handleBlockSlots = async () => {
    if (!blockDate || blockTimes.length === 0 || !staffId) return;
    setProcessing(true);

    const inserts = blockTimes.map(time => ({
      booking_date: blockDate,
      booking_time: time,
      client_name: "BLOQUEADO",
      client_email: "",
      client_phone: "",
      location_type: "studio",
      status: "blocked",
      staff_id: staffId,
      total_price: 0,
      delivery_fee: 0,
    }));

    const { error } = await supabase.from("bookings").insert(inserts);
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      toast({ title: `${blockTimes.length} horário(s) bloqueado(s)!` });
      setBlockOpen(false);
      setBlockDate("");
      setBlockTimes([]);
      // Refresh if viewing blocked
      if (filter === "blocked") {
        supabase.from("bookings").select("id, client_name, booking_date, booking_time, location_type, status, services:service_id(name)")
          .eq("staff_id", staffId).eq("status", "blocked").order("booking_date")
          .then(({ data }) => setBookings((data as any[]) || []));
      }
    }
    setProcessing(false);
  };

  const handleUnblock = async (bookingId: string) => {
    await supabase.from("bookings").delete().eq("id", bookingId);
    toast({ title: "Horário desbloqueado!" });
    setBookings(prev => prev.filter(b => b.id !== bookingId));
  };

  if (loading || (user && rolesLoading)) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A carregar...</div>;

  if (!isStaff) {
    if (staffStatus === "pending" || staffStatus === "suspended") {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-md w-full text-center rounded-2xl border border-border bg-card p-8 space-y-4">
            <ShieldAlert size={48} className="mx-auto text-amber-500" />
            <h1 className="font-display text-2xl font-bold">
              {staffStatus === "pending" ? "Acesso pendente de aprovação" : "Acesso suspenso"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {staffStatus === "pending"
                ? "A sua conta foi criada, mas o acesso ao painel só fica ativo depois de ser aprovado por um administrador da Mente Sã."
                : "O seu acesso ao painel do colaborador foi suspenso. Contacte a administração da Mente Sã."}
            </p>
            <Button variant="outline" className="rounded-full w-full gap-1" onClick={signOut}>
              <LogOut size={14} /> Sair
            </Button>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Mente Sã" className="h-9 w-9 rounded-full object-cover" />
            <div>
              <span className="font-display text-lg font-bold text-primary block leading-tight">Mente Sã</span>
              <span className="text-xs text-muted-foreground">Painel do Colaborador</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <User size={14} /> {staffName}
            </span>
            {staffRole && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {STAFF_ROLE_PRESETS[staffRole as StaffAccessRole]?.label || staffRole}
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={signOut} className="gap-1">
              <LogOut size={14} /> Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-2xl font-bold">
            {canViewAll && scope === "all" ? "Agenda da Equipa" : "A Minha Agenda"}
          </h1>
          <div className="flex gap-2">
            {canViewAll && (
              <Button
                size="sm"
                variant="outline"
                className="rounded-full gap-1.5"
                onClick={() => setScope(scope === "mine" ? "all" : "mine")}
              >
                <Users size={14} /> {scope === "mine" ? "Ver toda a equipa" : "Ver apenas as minhas"}
              </Button>
            )}
            {canBlock && (
              <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => setBlockOpen(true)}>
                <Lock size={14} /> Bloquear Horário
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {["confirmed", "pending", "on_the_way", "completed", "blocked", "cancelled"].map(s => (
            <Button
              key={s}
              variant={filter === s ? "default" : "outline"}
              size="sm"
              className="rounded-full"
              onClick={() => setFilter(s)}
            >
              {statusLabels[s]}
            </Button>
          ))}
        </div>

        {/* Bookings */}
        <div className="space-y-3">
          {bookings.length === 0 && (
            <p className="text-muted-foreground text-center py-12">
              Nenhum agendamento {statusLabels[filter]?.toLowerCase()} encontrado.
            </p>
          )}
          {bookings.map(b => (
            <div key={b.id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-foreground">
                    {b.status === "blocked" ? "🔒 Bloqueado" : b.client_name}
                  </h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[b.status]}`}>
                    {statusLabels[b.status]}
                  </span>
                </div>
                <div className="flex gap-3 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar size={13} />
                    {format(new Date(b.booking_date), "d MMM yyyy", { locale: pt })}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={13} /> {b.booking_time.slice(0, 5)}
                  </span>
                  {b.services && (
                    <span>{(b.services as any)?.name}</span>
                  )}
                  {can("view_client_contacts") && b.client_phone && <span>{b.client_phone}</span>}
                </div>
              </div>
              {b.status === "blocked" && b.staff_id === staffId && canBlock && (
                <Button size="icon" variant="ghost" onClick={() => handleUnblock(b.id)} title="Desbloquear">
                  <Trash2 size={16} />
                </Button>
              )}
            </div>
          ))}
        </div>
      </main>

      {/* Block Dialog */}
      <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Bloquear Meus Horários</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Data</Label>
              <Input type="date" value={blockDate} onChange={(e) => { setBlockDate(e.target.value); setBlockTimes([]); }} />
            </div>
            {blockDate && (
              <div className="space-y-2">
                <Label>Selecione os horários a bloquear</Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {getTimeSlotsForDateStr(blockDate).map(slot => {
                    const isBooked = bookedSlotsForBlock.includes(slot);
                    const isSelected = blockTimes.includes(slot);
                    return (
                      <button
                        key={slot}
                        disabled={isBooked}
                        onClick={() => toggleBlockTime(slot)}
                        className={`rounded-lg py-2 text-sm font-medium transition-all ${
                          isBooked
                            ? "bg-muted text-muted-foreground/40 cursor-not-allowed line-through"
                            : isSelected
                            ? "bg-destructive text-destructive-foreground"
                            : "border border-border hover:border-primary/50"
                        }`}
                      >
                        {slot}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <Button
              className="w-full rounded-full gap-2"
              disabled={!blockDate || blockTimes.length === 0 || processing}
              onClick={handleBlockSlots}
            >
              <Lock size={14} /> {processing ? "A bloquear..." : `Bloquear ${blockTimes.length} horário(s)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffPortal;
